import { useEffect, useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import ListingsCarousel, { CarouselListing } from "./ListingsCarousel";
import { Coords, distanceKm, geocodeCity, geocodePostalCode } from "@/lib/geo";

const SELECT = "id, title, brand, model, price, city, images, condition, dgt_approved, created_at, latitude, longitude";

type Props = {
  defaultRadiusKm: number;
  monthlyRate: number;
  autoplay: boolean;
};

const NearYouCarousel = ({ defaultRadiusKm, monthlyRate, autoplay }: Props) => {
  const { user } = useAuth();
  const [pool, setPool] = useState<(CarouselListing & { latitude: number | null; longitude: number | null })[]>([]);
  const [userCoords, setUserCoords] = useState<Coords | null>(null);
  const [radius, setRadius] = useState(defaultRadiusKm);
  const [loading, setLoading] = useState(true);
  const [hydratedRadius, setHydratedRadius] = useState(false);

  useEffect(() => {
    if (!hydratedRadius) setRadius(defaultRadiusKm);
  }, [defaultRadiusKm, hydratedRadius]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      // Fetch user coords + preferred radius from profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("city, postal_code, preferred_radius_km")
        .eq("user_id", user.id)
        .maybeSingle();

      if (profile && (profile as any).preferred_radius_km) {
        setRadius((profile as any).preferred_radius_km as number);
        setHydratedRadius(true);
      }

      let uc: Coords | null = null;
      if (profile?.postal_code) uc = await geocodePostalCode(profile.postal_code);
      if (!uc && profile?.city) uc = await geocodeCity(profile.city);
      setUserCoords(uc);

      // Fetch listings with coords already (instant). Fallback to coordless rows that we'll geocode by city only if needed.
      const { data: withCoords } = await supabase
        .from("listings")
        .select(SELECT)
        .eq("status", "active")
        .not("latitude", "is", null)
        .limit(300);

      let merged = (withCoords as any[]) || [];

      // If we have very few geocoded listings, also pull a few coordless and resolve via cached city geocoding (best effort).
      if (uc && merged.length < 50) {
        const { data: rest } = await supabase
          .from("listings")
          .select(SELECT)
          .eq("status", "active")
          .is("latitude", null)
          .limit(100);
        const restRows = (rest as any[]) || [];
        const uniqueCities = Array.from(new Set(restRows.map((r) => r.city).filter(Boolean))).slice(0, 25);
        const cityMap = new Map<string, Coords | null>();
        await Promise.all(uniqueCities.map(async (c) => cityMap.set(c, await geocodeCity(c))));
        const enriched = restRows
          .map((r) => {
            const c = cityMap.get(r.city);
            return c ? { ...r, latitude: c.lat, longitude: c.lon } : null;
          })
          .filter(Boolean) as any[];
        merged = [...merged, ...enriched];
      }

      setPool(merged);
      setLoading(false);
    })();
  }, [user]);

  const filtered = useMemo<CarouselListing[]>(() => {
    if (!userCoords || pool.length === 0) return [];
    return pool
      .map((l) => ({
        l,
        dist: distanceKm(userCoords, { lat: Number(l.latitude), lon: Number(l.longitude) }),
      }))
      .filter((x) => x.dist <= radius)
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 12)
      .map((x) => ({ ...x.l, distanceKm: x.dist }));
  }, [userCoords, pool, radius]);

  // Persist preferred radius to profile (debounced)
  useEffect(() => {
    if (!user) return;
    const t = window.setTimeout(() => {
      supabase
        .from("profiles")
        .update({ preferred_radius_km: radius } as any)
        .eq("user_id", user.id);
    }, 600);
    return () => window.clearTimeout(t);
  }, [radius, user]);

  if (!user) return null;

  return (
    <section className="border-t border-border bg-background py-6">
      <div className="container">
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-3">
          <MapPin className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Radio de búsqueda:</span>
          <span className="text-sm font-bold text-primary">{radius} km</span>
          <div className="ml-auto w-full max-w-[260px]">
            <Slider
              value={[radius]}
              min={5}
              max={500}
              step={5}
              onValueChange={(v) => setRadius(v[0])}
              aria-label="Radio en kilómetros"
            />
          </div>
        </div>
      </div>
      <ListingsCarousel
        title="Cerca de ti"
        subtitle={userCoords ? `Anuncios en un radio de ${radius} km` : "Configura tu código postal en tu perfil para ver anuncios cercanos"}
        listings={filtered}
        loading={loading}
        monthlyRate={monthlyRate}
        emptyHint={userCoords ? "No hay anuncios en este radio. Aumenta el radio." : "Añade tu código postal al perfil."}
        showViewAll={false}
        autoplay={autoplay}
      />
    </section>
  );
};

export default NearYouCarousel;
